import { useParams } from 'react-router';

import {
  ConstructionCompanyDetailForm,
  ConstructionCompanyDetailResponse,
  NoharaRepresentative,
} from '../../types/company.types';
import { DepartmentContact } from '../../types/table/company.types';
import { validateConstructionRegistrationForm } from '../../utils/validations/construction-validation';
import {
  mappingEditConstructionCompanyData,
  EditConstructionCompanyRequest,
} from '../../utils/mappers/construction-form-mapper';
import { companiesService } from '../../services/companies.service';
import {
  useCompanyDetailFormBase,
  CompanyDetailFormConfig,
} from '../common/use-company-detail-form-base';

const initialFormData: ConstructionCompanyDetailForm = {
  galiCustomerCode: '',
  officialName: '',
  abbreviatedName: '',
  nameKatakana: '',
  isCommonCement: false,
  representatives: [
    {
      department: '',
      salesPerson: {
        id: '',
        firstName: '',
        lastName: '',
        code: '',
      },
    },
  ] as NoharaRepresentative[],
};

export const useConstructionDetailForm = () => {
  const { constructionCompanyId } = useParams<{ constructionCompanyId: string }>();

  const config: CompanyDetailFormConfig<
    ConstructionCompanyDetailForm,
    ConstructionCompanyDetailResponse,
    EditConstructionCompanyRequest
  > = {
    companyId: constructionCompanyId,
    initialFormData,
    validateForm: validateConstructionRegistrationForm,
    fetchCompanyById: (id: string) => companiesService.getConstructionCompanyById(id),
    updateCompanyById: (id: string, data: EditConstructionCompanyRequest) =>
      companiesService.updateConstructionCompanyById(id, data),
    mapApiResponseToFormData: (apiData, findDepartmentIdByCode) => ({
      galiCustomerCode:
        apiData.galiAccountCode !== null && apiData.galiAccountCode !== undefined
          ? apiData.galiAccountCode.toString()
          : '',
      officialName: apiData.name || '',
      abbreviatedName: apiData.nameAbbreviation || '',
      nameKatakana: apiData.nameKana || '',
      isCommonCement: apiData.isCommonCement || false,
      canUpdate: apiData.canUpdate ?? false,
      representatives:
        apiData.departmentContactLists?.map((contact: DepartmentContact, index: number) => {
          const department = findDepartmentIdByCode(contact.departmentCode);
          const departmentId = department ? department : (contact.departmentId ?? '');

          // If department is missing or not found, clear salesPerson to make it an empty representative
          // This ensures validation will skip it (empty representatives are skipped)
          const hasValidDepartment = !!departmentId;

          const staff =
            hasValidDepartment && contact.contactPersonCode
              ? {
                  id: contact.saleStaff.id,
                  lastName: contact.saleStaff.lastName,
                  firstName: contact.saleStaff.firstName,
                  code: contact.contactPersonCode.toString(),
                }
              : {
                  id: '',
                  lastName: '',
                  firstName: '',
                  code: '',
                };

          return {
            department: departmentId,
            departmentName: contact.departmentName,
            departmentCode: contact.departmentCode.toString(),
            canView: contact.canView,
            salesPerson: staff,
            canUpdate: contact.canUpdate,
            originalIndex: index,
          };
        }) ||
        ([
          {
            department: '',
            salesPerson: {
              id: '',
              lastName: '',
              firstName: '',
              code: '',
            },
          },
        ] as NoharaRepresentative[]),
    }),
    mapFormDataToApiRequest: mappingEditConstructionCompanyData,
    getDepartmentContactLists: (apiData) => apiData.departmentContactLists,
  };

  const result = useCompanyDetailFormBase(config);

  return {
    ...result,
    // Rename companyId to constructionCompanyId for backward compatibility
    constructionCompanyId: result.companyId,
  };
};
